/**
 * **Le choix explicite** : bac à sable, ou compte.
 *
 * Jusqu'ici `runStore` importait `localGameClient` **en dur** — c'est ce qui rendait les neuf
 * routes `/api/*` et toute l'authentification strictement mortes. Ce module est l'aiguillage
 * qui manquait : un seul endroit décide quelle implémentation de `GameClient` répond, et
 * personne d'autre n'importe une implémentation.
 *
 * ## Pourquoi un choix, et pas une bascule
 *
 * Le bac à sable n'est pas un brouillon dont on se débarrasse : c'est **ce qui rend le jeu
 * testable sans base**, et c'est aussi le seul mode où l'on peut rejouer une graine choisie,
 * où le mode triche (ex-télécommande de dev, ⟦A-176⟧) fonctionne, et où l'on produit une vidéo aujourd'hui. Le compte,
 * lui, est le seul mode où le hardcore veut dire quelque chose, où une victoire se grave et où
 * la partie survit à un changement d'appareil (PLAN §6).
 *
 * Les deux restent donc côte à côte, et **le joueur sait toujours dans lequel il est** — c'est
 * la seule chose qu'on ne s'autorise pas à laisser flou : le même écran avec deux significations
 * différentes de « game over », ce serait mentir.
 *
 * ## La règle d'or de ce fichier
 *
 * On ne **change jamais** de client pendant qu'une run est chargée sans repasser par
 * `runStore.charger()` : les deux implémentations ont chacune leur miroir, et l'une ne connaît
 * rien de l'autre. `definirMode` remet donc le client actif à zéro, et l'appelant recharge.
 */

import type { GameClient } from "./gameClient";
import { gameClient as clientLocal, bacASable } from "./localGameClient";
import { ServerGameClient } from "./serverGameClient";
import { me, reglerPreferences, reglerRun } from "../api/jeu";
import { useUIStore, type ModeRevelation } from "../stores/uiStore";

export type ModeJeu = "bac-a-sable" | "compte";

/** Le mode choisi par le joueur, mémorisé pour ne pas reposer la question à chaque visite. */
const CLE_MODE = "maxime.mode.v1";

const clientServeur = new ServerGameClient();

/**
 * Le client actif. Défaut = bac à sable : sans réseau, sans compte, sans base, le jeu se joue
 * quand même — c'est la propriété qu'on refuse de perdre.
 */
let actif: GameClient = clientLocal;

/** L'implémentation qui répond en ce moment. **Le seul accès** pour les stores et les écrans. */
export function clientDeJeu(): GameClient {
  return actif;
}

/** Le mode en cours d'utilisation, déduit du client actif (jamais du stockage). */
export function modeActif(): ModeJeu {
  return actif.kind === "serveur" ? "compte" : "bac-a-sable";
}

/** Le mode mémorisé, ou `null` si le joueur n'a **jamais** choisi (première visite). */
export function modeMemorise(): ModeJeu | null {
  try {
    const brut = localStorage.getItem(CLE_MODE);
    return brut === "compte" || brut === "bac-a-sable" ? brut : null;
  } catch {
    return null;
  }
}

/**
 * Choisit le mode. **Ne charge rien** : l'appelant (le store de session) enchaîne sur
 * `runStore.charger()`, qui interrogera la bonne implémentation.
 */
export function definirMode(mode: ModeJeu): void {
  actif = mode === "compte" ? clientServeur : clientLocal;
  try {
    localStorage.setItem(CLE_MODE, mode);
  } catch {
    // Mode privé : le choix ne survivra pas à l'onglet. La partie, elle, s'en moque.
  }
}

/** Oublie le choix — on repose la question à la prochaine visite (après une déconnexion). */
export function oublierMode(): void {
  actif = clientLocal;
  try {
    localStorage.removeItem(CLE_MODE);
  } catch {
    // Rien à faire : sans stockage, le choix était déjà volatil.
  }
}

/**
 * ⟦SPEC-API §5.9⟧ Répercuter la **tolérance d'accessibilité** sur la run du compte.
 *
 * Côté serveur, la tolérance n'est pas un réglage d'affichage : c'est
 * `flags.accessibility_tolerance` dans l'état, lu à la génération du pattern ET au recalcul du
 * score (§6.1). Un interrupteur d'interface qui ne le dirait pas au serveur serait un
 * interrupteur qui ne fait rien — exactement la coquille vide qu'on est en train de fermer.
 *
 * Silencieux en bac à sable (rien à synchroniser) et silencieux si le réseau tombe : c'est un
 * confort, pas une règle, et il se rattrapera au prochain basculement.
 */
export async function synchroniserTolerance(active: boolean): Promise<void> {
  if (actif.kind !== "serveur") return;
  const run = actif.getState();
  if (!run) return;
  try {
    await reglerRun(run.runId, { assist_tolerance: active });
  } catch {
    // Réglage non répercuté — on ne casse pas l'écran des options pour ça.
  }
}

/**
 * La **même instance** de bac à sable, typée large, pour ce qui lui est explicitement propre
 * (le mode triche, `game/dev.ts`). Ré-exportée ici pour qu'un seul module reste le point d'entrée des
 * clients de jeu — et pour que l'appelant vérifie `modeActif()` avant de s'en servir.
 */
export { bacASable };

/**
 * ⟦A-8 / M2.40 · A-81⟧ Répercuter la **préférence de révélation** sur le compte.
 *
 * C'est une préférence du JOUEUR, pas de la run (le mot de Mathieu) : elle ne passe par aucune
 * action, elle va à `PATCH /api/me/preferences`. Silencieux en bac à sable (le localStorage est
 * sa seule mémoire) et silencieux si le réseau tombe : le prochain boot relira le serveur, et
 * un réglage d'affichage ne vaut pas une erreur à l'écran.
 */
export async function synchroniserRevelation(mode: ModeRevelation): Promise<void> {
  if (actif.kind !== "serveur") return;
  try {
    await reglerPreferences({ reveal_mode: mode });
  } catch {
    // Préférence non répercutée — le cache local la garde, le serveur la reprendra.
  }
}

/**
 * ⟦C8 · A-120⟧ Répercuter le **minuteur du manager** sur le compte — même patron que
 * `synchroniserRevelation` juste au-dessus, et pour la même raison : une préférence du
 * JOUEUR, pas de la run, silencieuse en bac à sable et si le réseau tombe.
 */
export async function synchroniserManagerDelai(actifBool: boolean): Promise<void> {
  if (actif.kind !== "serveur") return;
  try {
    await reglerPreferences({ manager_delai_actif: actifBool });
  } catch {
    // Préférence non répercutée — le cache local la garde, le serveur la reprendra.
  }
}

/**
 * ⟦A-8 / M2.40 · A-81⟧ **Relire les préférences du compte au boot** — « persistée côté compte
 * et relue au boot du client ». Appelé par le store de session à chaque entrée en mode compte ;
 * ce qu'il lit écrase le cache local, sans renvoyer au serveur ce qu'il vient d'en recevoir.
 * Un réseau qui tombe laisse le cache tel quel : la partie reste jouable.
 */
export async function chargerPreferencesCompte(): Promise<void> {
  if (actif.kind !== "serveur") return;
  try {
    const { preferences } = await me();
    useUIStore.getState().adopterPreferencesCompte({
      revealMode: preferences.reveal_mode,
      managerDelaiActif: preferences.manager_delai_actif,
    });
  } catch {
    // On garde le cache local : un boot sans réseau ne doit pas effacer un choix.
  }
}
